// This component houses the dropzones for table fields
// Number of dropzones and its name is returned according to the chart type selected.
// Once minimum number of fields are met for the given chart type, server call is made to get chart data and saved in store
// This is another comment

import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import ChartsInfo from './ChartsInfo2';
import './ChartAxes.css';
import DropZone from './DropZone/DropZone';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import _ from 'lodash';
import { ChartAxesProps } from './ChartAxesInterfaces';
import ChartData from './ChartData';
import {
  canReUseData,
  changeGeoMapUnMatched,
  reorderParameterizedCharts,
} from '../../redux/ChartPoperties/ChartPropertiesActions'; // adjust the path as per your project structure

import {
  changeLocation,
  changeGeoKey,
  enableOverrideForUIDAction,
  editChartPropItem,
  removeChartAxesForUID,
} from '../../redux/ChartPoperties/ChartPropertiesActions';
import { getMismachedLocationArray } from '../Charts/GeoChart/GeoJSON/MapCommonFunctions';
import WarningIcon from '@mui/icons-material/WarningAmber';
import GoeMismatch from '../Charts/GeoChart/Components/GeoMismatch';
import GoeHelp from '../Charts/GeoChart/Components/GeoHelp';
import { displayName } from '../CommonFunctions/CommonFunctions';
import { InfoOutlined } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { Button } from '@mui/material';
import CalculationFunctionListHolder from '../Calculations/CalculationFunctionsListHolder/CalculationFunctionsListHolder';
import { palette } from '../..';
import ShowLocationPicker from './ShowLocationPicker';
import { useSwitchAxesForCharts } from '../ChartOptions/ChartTypeComponents/useSwitchAxesUtils';
import {
  callQueryAPI,
  SetModifyOptions,
  updateChartData,
  updateLabelOption,
  updateExtraMeasureField,
  enableOverrideForExtraMeasure,
} from '../../redux/ChartPoperties/ChartControlsActions';
import { TAlert } from '../../types/types';
import { NotificationDialog } from '../CommonFunctions/DialogComponents';

const ChartAxes = ({
  // props
  tabId,
  tileId,
  uID,
  mapJSON,
  tabTileProps,
  isExtraMeasure = false,
}: ChartAxesProps & any) => {
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const chartProp = useSelector((state: any) => state.chartProperties);
  const chartControls = useSelector((state: any) => state.chartControls);
  const calculations = useSelector((state: any) => state.calculations);
  const parameters = useSelector((state: any) => state.parameters);

  let propKey: string = `${tabId}.${tileId}`;
  let dropZones: string[] = [];

  const [mapKeys, setMapKeys] = useState<any>([]);
  const [anchorHelpElm, setAnchorHelpElm] = useState<any | null>(null);
  const [anchorMismatchElm, setAnchorMismatchElm] = useState<any | null>(null);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [showOptionsMismatch, setShowOptionsMismatch] = useState<boolean>(false);
  const [showMismatchIcon, setShowMismatchIcon] = useState<boolean>(false);
  const [misMatchList, setMismatchList] = useState<any>([]);
  // const [isHelpHovered, setIsHelpHovered] = useState(false);
  const [isUnMatchedFixed, setIsUnMatchedFixed] = useState(false);
  const dimensionName = chartProp.properties[propKey].chartAxes[1].fields[0]?.displayname;
  const [alert, setAlert] = useState<TAlert | null>(null);

  let chartControl: any = chartControls.properties[propKey];
  let chartData: any[] = chartControl?.chartData ? chartControl.chartData : [];

  for (let i = 0; i < ChartsInfo[chartProp.properties[propKey].chartType].dropZones.length; i++) {
    if (ChartsInfo[chartProp.properties[propKey].chartType].dropZones[i].name === 'X Axis') {
      dropZones.push('X');
    } else if (ChartsInfo[chartProp.properties[propKey].chartType].dropZones[i].name === 'Y Axis') {
      dropZones.push('Y');
    } else {
      dropZones.push(ChartsInfo[chartProp.properties[propKey].chartType].dropZones[i].name);
    }
  }

  const handleClose = () => {
    setAnchorHelpElm(null);
    setShowOptions(false);
  };

  const handleCloseWarningMismatch = () => {
    setAnchorMismatchElm(null);
    setShowOptionsMismatch(false);
  };

  const currentCalculationSession = calculations.properties[propKey]?.currentCalculationSession;

  let menuItemStyle = {
    fontSize: '12px',
    padding: '2px 1rem',
  };

  useEffect(() => {
    if (mapJSON?.features) {
      const props = mapJSON?.features?.[0].properties || {};
      let extraKeys = Object.keys(mapJSON?.features?.[0].properties)
        .filter((item) =>
          ['continent', 'hc-a2', 'iso-a2', 'iso-a3', 'region-wb', 'subregion'].includes(item),
        )
        .filter((key) => props[key] !== null && props[key] !== undefined && props[key] !== '');
      extraKeys.sort();

      const isCitiesMap = chartProp.properties[propKey].Geo.geoLocation.includes('Cities');
      const currentSelectedKey = chartProp.properties[propKey].Geo.geoMapKey;

      let validKeysForThisMap: string[] = [];

      if (isCitiesMap) {
        // If it's a City map, the only valid key is usually 'city'
        validKeysForThisMap = ['city'];
        setMapKeys(validKeysForThisMap);

        if (currentSelectedKey !== 'city') {
          dispatch(changeGeoKey(propKey, 'city'));
        }
      } else {
        // If it's World/Country, valid keys are 'name' + extras
        validKeysForThisMap = ['name', ...extraKeys];
        setMapKeys(validKeysForThisMap);

        // Check if the currently selected key (e.g., 'city') exists in the new valid keys.
        // If it does NOT exist, force the selection to 'name'.
        if (!validKeysForThisMap.includes(currentSelectedKey)) {
          dispatch(changeGeoKey(propKey, 'name'));
        }
      }
    }
    // Added chartType to dependency so this runs immediately when switching tabs
  }, [mapJSON, chartProp.properties[propKey].chartType]);
  // useEffect(() => {
  //   dispatch(updateLabelOption(propKey, 'showLabel', false));
  // }, [chartControls.properties[propKey].labelOptions.showLabel]);

  useEffect(() => {
    const hasNullInMismatch = misMatchList?.some((item: any) => {
      return item?.region === null || item?.region === '' || item?.[dimensionName] === null;
    });

    if (hasNullInMismatch) {
      setIsUnMatchedFixed(false);
    }
  }, [misMatchList, dimensionName]);

  useEffect(() => {
    let misMatchArray = [];

    if (
      (chartProp.properties[propKey].chartType === 'filledMap' ||
        chartProp.properties[propKey].chartType === 'bubbleMap') &&
      chartData.length > 0
    ) {
      let dimensionName = chartProp.properties[propKey].chartAxes[1].fields[0];
      if (chartProp.properties[propKey].chartType === 'bubbleMap')
        dimensionName = chartProp.properties[propKey].chartAxes[2].fields[0];

      if (chartProp.properties[propKey].Geo.geoMapKey === 'city') {
      }

      const mapJson = mapJSON;

      misMatchArray = getMismachedLocationArray(
        chartData,
        displayName(dimensionName),
        chartProp.properties[propKey].Geo.geoLocation,
        chartProp.properties[propKey].Geo.geoMapKey,
        mapJson,
      );

      ////TODO save to redux
      setShowMismatchIcon(misMatchArray.length > 0);
      setMismatchList(misMatchArray);

      const unMatched = chartProp.properties[propKey].Geo.unMatchedChartData ?? [];

      if (unMatched.length === 0) {
        setIsUnMatchedFixed(false);
      } else {
        if (chartProp.properties[propKey].Geo.unMatchedChartData?.length > 0) {
          let emptySelectedKey = chartProp.properties[propKey].Geo.unMatchedChartData.find(
            (item: any) => item.selectedKey == '',
          );
          setIsUnMatchedFixed(!emptySelectedKey);
        }
      }
    } else {
      setShowMismatchIcon(false);
    }
  }, [chartData, chartProp.properties[propKey].Geo, mapJSON]);

  // const handleLocationOnChange = (e: any) => {
  //   dispatch(changeLocation(propKey, e.currentTarget.innerText));
  // };

  const handleLocationOnChange = (value: string) => {
    if (!value) return; // Ignore empty values

    dispatch(changeLocation(propKey, value));
    handleGeoMapUnMatchedReset();
    setIsUnMatchedFixed(false);
    // if (
    //   e.currentTarget.innerText.toLowerCase() === 'world' ||
    //   e.currentTarget.innerText.includes('Cities')
    // ) {
    //   dispatch(updateLabelOption(propKey, 'showLabel', false));
    // } else {
    //   dispatch(updateLabelOption(propKey, 'showLabel', true));
    // }
    dispatch(updateLabelOption(propKey, 'showLabel', false));
  };

  const handleGeoMapUnMatchedReset = () => {
    // grab the original array
    const data = chartProp.properties[propKey].Geo.unMatchedChartData || [];

    // make a new array with selectedKey cleared
    const resetArray = data.map((item: any) => ({
      ...item,
      selectedKey: '', // overwrite with empty string
    }));
    // dispatch once with the whole new array
    dispatch(changeGeoMapUnMatched(propKey, resetArray));
  };

  const handleOverrideSave = () => {
    dispatch(SetModifyOptions(propKey, 'filter_override_close', null));
    dispatch(enableOverrideForUIDAction(propKey, ''));

    if (chartProp.properties[propKey].chartType !== 'scatterPlot') {
      let bIndex = ChartsInfo[chartProp.properties[propKey].chartType].dropZones.findIndex(
        (item: any) => item.name === 'Measure',
      );
      let itemIndex = chartProp.properties[propKey].chartAxes[bIndex].fields.findIndex(
        (item: any) => item.uId === uID,
      );
      let field = chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex];
      let tempField: any = {};

      if (field) {
        tempField = JSON.parse(JSON.stringify(field));
        tempField.override = chartProp.properties[propKey]['chartAxes_' + uID];
        // tempField.disableReportFilterForOverride = true
        dispatch(
          editChartPropItem('updateQuery', {
            propKey,
            binIndex: bIndex,
            itemIndex,
            item: tempField,
            currentChartAxesName: 'chartAxes',
          }),
        );
        dispatch(callQueryAPI(propKey, true));
      }
    } else {
      let bIndexX = chartProp.properties[propKey].chartAxes[2].fields.findIndex(
        (item: any) => item.uId === uID,
      );
      let bIndexY = chartProp.properties[propKey].chartAxes[3].fields.findIndex(
        (item: any) => item.uId === uID,
      );
      let bIndex = 2,
        itemIndex = 0;

      if (bIndexX > -1) {
        bIndex = 2;
        itemIndex = 0;
      }

      if (bIndexY > -1) {
        bIndex = 3;
        itemIndex = 0;
      }

      let field = chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex];
      let tempField: any = {};

      if (field) {
        tempField = JSON.parse(JSON.stringify(field));
        tempField.override = chartProp.properties[propKey]['chartAxes_' + uID];
        // tempField.disableReportFilterForOverride = true
        dispatch(
          editChartPropItem('updateQuery', {
            propKey,
            binIndex: bIndex,
            itemIndex,
            item: tempField,
            currentChartAxesName: 'chartAxes',
          }),
        );
        dispatch(callQueryAPI(propKey, true));
      }
    }

    dispatch(removeChartAxesForUID(propKey, uID));
  };

  const handleOverrideRemove = () => {
    dispatch(SetModifyOptions(propKey, 'filter_override_close', null));
    dispatch(enableOverrideForUIDAction(propKey, ''));

    if (chartProp.properties[propKey].chartType !== 'scatterPlot') {
      // Loop over parameter.savedParameter.chartFilters.currentChartAxesName and check if any includes uID
      const savedParameters = parameters.savedParameters;
      savedParameters.forEach((parameter: any) => {
        if (parameter.chartFilter) {
          const chartFilter = parameter.chartFilter.filter(
            (filter: any) => !filter.currentChartAxesName.includes(uID),
          );
          if (
            parameter.chartFilter.find((filter: any) => filter.currentChartAxesName.includes(uID))
          ) {
            dispatch(reorderParameterizedCharts(propKey, parameter.parameterId, chartFilter));
          }
        }
      });

      let bIndex = ChartsInfo[chartProp.properties[propKey].chartType].dropZones.findIndex(
        (item: any) => item.name === 'Measure',
      );
      let itemIndex = chartProp.properties[propKey].chartAxes[bIndex].fields.findIndex(
        (item: any) => item.uId === uID,
      );
      let field = chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex];
      let tempField: any = {};

      if (field) {
        tempField = JSON.parse(JSON.stringify(field));
        tempField.override = null;
        tempField.disableReportFilterForOverride = false;
        dispatch(
          editChartPropItem('updateQuery', {
            propKey,
            binIndex: bIndex,
            itemIndex,
            item: tempField,
            currentChartAxesName: 'chartAxes',
          }),
        );
        dispatch(callQueryAPI(propKey, true));
      }
    } else {
      let bIndexX = chartProp.properties[propKey].chartAxes[2].fields.findIndex(
        (item: any) => item.uId === uID,
      );
      let bIndexY = chartProp.properties[propKey].chartAxes[3].fields.findIndex(
        (item: any) => item.uId === uID,
      );
      let bIndex = 2,
        itemIndex = 0;

      if (bIndexX > -1) {
        bIndex = 2;
        itemIndex = 0;
      }

      if (bIndexY > -1) {
        bIndex = 3;
        itemIndex = 0;
      }

      let field = chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex];
      let tempField: any = {};

      if (field) {
        tempField = JSON.parse(JSON.stringify(field));
        tempField.override = null;
        tempField.disableReportFilterForOverride = false;
        dispatch(
          editChartPropItem('updateQuery', {
            propKey,
            binIndex: bIndex,
            itemIndex,
            item: tempField,
            currentChartAxesName: 'chartAxes',
          }),
        );
        dispatch(callQueryAPI(propKey, true));
      }
    }

    dispatch(removeChartAxesForUID(propKey, uID));
  };

  const handleExtraMeasureOverrideAction = (overrideValue: any) => {
    dispatch(SetModifyOptions(propKey, 'filter_override_close', null));
    dispatch(enableOverrideForExtraMeasure(propKey, ''));

    const tooltipFormats = chartControls.properties[propKey]?.formatOptions?.tooltipFormats;
    const extraMeasureField = tooltipFormats?.extraMeasureField;

    let bIndex = -1;
    let itemIndex = -1;

    if (extraMeasureField) {
      for (let i = 0; i < extraMeasureField.length; i++) {
        const fields = extraMeasureField[i].fields;
        if (fields) {
          const idx = fields.findIndex((item: any) => item.uId === uID);
          if (idx !== -1) {
            bIndex = i;
            itemIndex = idx;
            break;
          }
        }
      }
    }

    if (bIndex !== -1 && itemIndex !== -1) {
      let field = extraMeasureField[bIndex].fields[itemIndex];
      let tempField: any = {};

      if (field) {
        tempField = JSON.parse(JSON.stringify(field));
        tempField.override = overrideValue;
        tempField.disableReportFilterForOverride = false;
        dispatch(updateExtraMeasureField(propKey, bIndex, itemIndex, tempField));
      }
    }
    dispatch(removeChartAxesForUID(propKey, uID));
    dispatch(callQueryAPI(propKey, true));
  };

  const handleExtraMeasureOverrideRemove = () => {
    const overrideValue = null;
    handleExtraMeasureOverrideAction(overrideValue);
  };

  // Helper: compare Search Condition filter fields
  const compareSearchCondition = (b: any, o: any) => {
    if (b.exprType !== o.exprType) return false;
    const isDateType = b.dataType === 'date' || b.dataType === 'timestamp';
    const isBetween = b.exprType === 'between';
    if (isBetween) {
      const isRangeEqual =
        b.greaterThanOrEqualTo === o.greaterThanOrEqualTo &&
        b.lessThanOrEqualTo === o.lessThanOrEqualTo;

      return isDateType ? isRangeEqual && b.prefix === o.prefix : isRangeEqual;
    }
    const isInputEqual = b.exprInput === o.exprInput;
    return isDateType ? isInputEqual && b.prefix === o.prefix : isInputEqual;
  };

  // Helper: compare Pick List filter fields
  const comparePickList = (b: any, o: any) => {
    if ((b.dataType === 'date' || b.dataType === 'timestamp') && b.prefix !== o.prefix)
      return false;

    return _.isEqual(_.sortBy(b.userSelection || []), _.sortBy(o.userSelection || []));
  };

  // Helper: compare Relative Filter fields
  const compareRelativeFilter = (b: any, o: any) => {
    return (
      b?.fromDate === o?.fromDate &&
      b?.toDate === o?.toDate &&
      b?.expTypeAnchorDate === o?.expTypeAnchorDate
    );
  };

  // this will check override and base is same or not if same then return true else false
  function isOverrideSameAsBase(chartProp: any, propKey: string, uID: string) {
    if (!uID || !propKey || !chartProp.properties[propKey]) return true;

    const baseAxes = chartProp.properties[propKey].chartAxes;
    const overrideAxes = chartProp.properties[propKey][`chartAxes_${uID}`];

    if (!Array.isArray(overrideAxes)) return true;

    // Axes we do NOT compare
    const ignoreAxes = ['Measure', 'Split By', 'X Axis', 'Y Axis'];

    const baseFiltered = baseAxes.filter((a: any) => !ignoreAxes.includes(a.name));
    const overrideFiltered = overrideAxes.filter((a) => !ignoreAxes.includes(a.name));

    // MAIN AXIS COMPARISON LOOP
    for (let i = 0; i < baseFiltered.length; i++) {
      const baseAxis = baseFiltered[i];
      const overrideAxis = overrideFiltered[i];

      if (baseAxis?.fields?.length !== overrideAxis?.fields?.length) return false;

      // Track which override fields have already been matched
      const used = new Array(overrideAxis.fields.length).fill(false);

      for (let j = 0; j < baseAxis.fields.length; j++) {
        const baseField = baseAxis.fields[j];
        let matched = false;

        for (let k = 0; k < overrideAxis.fields.length; k++) {
          if (used[k]) continue;

          const overrideField = overrideAxis.fields[k];

          // Must match tableId, fieldname and dataType if anything not match then skip
          if (
            baseField.tableId !== overrideField.tableId ||
            baseField.fieldname !== overrideField.fieldname ||
            baseField.dataType !== overrideField.dataType
          )
            continue;

          // FILTER AXIS TYPE MATCHING
          if (baseAxis.name === 'Filter') {
            const type = baseField.fieldtypeoption;
            if (type !== overrideField.fieldtypeoption) continue;

            if (type === 'Search Condition' && !compareSearchCondition(baseField, overrideField))
              continue;

            if (type === 'Pick List' && !comparePickList(baseField, overrideField)) continue;

            if (type === 'Relative Filter' && !compareRelativeFilter(baseField, overrideField))
              continue;
          }

          // DATE FIELD (non-filter axis)
          if (
            baseAxis.name !== 'Filter' &&
            (baseField.dataType === 'date' || baseField.dataType === 'timestamp') &&
            baseField.timeGrain !== overrideField.timeGrain
          ) {
            continue;
          }

          // MATCH SUCCESS
          used[k] = true;
          matched = true;
          break;
        }

        if (!matched) return false;
      }
    }

    return true;
  }

  // save override
  const handleSave = () => {
    // run comparison
    const isSame = isOverrideSameAsBase(chartProp, propKey, uID);
    if (isSame) {
      setAlert({
        severity: 'warning',
        message: "Base and Override can't be the same.",
      });
      return;
    } else {
      if (isExtraMeasure) {
        const overrideValue = chartProp.properties[propKey]['chartAxes_' + uID];
        handleExtraMeasureOverrideAction(overrideValue);
      } else {
        handleOverrideSave();
      }
    }
  };

  return (
    !parameters.properties[propKey]?.parameterSession && (
      <div
        className={`charAxesArea ${uID ? 'overrideContainer' : ''} ${currentCalculationSession ? 'scrollOnHover' : ''}`}
        style={{
          width: '15.625rem',
          borderRight: isExtraMeasure ? 'unset' : '',
          borderLeft: isExtraMeasure ? '2px solid rgba(224, 224, 224, 1)' : '',
          scrollbarGutter: currentCalculationSession ? 'stable' : 'auto',
        }}
      >
        {!currentCalculationSession &&
        !uID &&
        (chartProp.properties[propKey].chartType === 'filledMap' ||
          chartProp.properties[propKey].chartType === 'bubbleMap') ? (
          <ShowLocationPicker
            value={chartProp.properties[propKey].Geo.geoLocation}
            onChange={handleLocationOnChange}
            chartType={
              chartProp.properties[propKey].chartType === 'filledMap' ? 'filled' : 'bubble'
            }
          />
        ) : null}
        {!currentCalculationSession &&
          !uID &&
          (chartProp.properties[propKey].chartType === 'filledMap' ||
            chartProp.properties[propKey].chartType === 'bubbleMap') && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                padding: '8px 12px 0 12px',
              }}
            >
              <FormControl
                sx={{
                  width: '100%',
                  margin: '0.5rem 0',
                  '& .MuiInputBase-root': {
                    borderRadius: '0px',
                    color: palette.primary.contrastText,
                  },
                  '& .MuiInputBase-input': {
                    padding: '0px 32px 0px 0px',
                  },
                }}
                style={{
                  background: 'white',
                  fontSize: '12px',
                  borderRadius: '4px',
                  padding: '2px 0px',
                }}
              >
                <InputLabel
                  sx={{
                    // fontSize: "1rem",
                    lineHeight: '1.5rem',
                    '&.Mui-focused': {
                      color: '#2bb9bb',
                    },
                  }}
                >
                  Select Key
                </InputLabel>
                <div style={{ display: 'flex' }}>
                  <div
                    style={{
                      position: 'absolute',
                      zIndex: 10,
                      top: '4px',
                      right: '40px',
                    }}
                  >
                    {showMismatchIcon ? (
                      <Tooltip
                        title="Unmatched Locations"
                        arrow
                        placement="top"
                        slotProps={{
                          tooltip: {
                            sx: {
                              fontSize: '12px',
                            },
                          },
                        }}
                      >
                        <WarningIcon
                          style={{
                            // marginLeft: '4px',
                            cursor: 'pointer',
                            marginTop: '4px',
                            color: isUnMatchedFixed ? 'green' : 'orange',
                            fontSize: '1em',
                            transition: 'color 0.3s, font-size 0.3s',
                          }}
                          onClick={(event) => {
                            setAnchorMismatchElm(event.currentTarget);
                            setShowOptionsMismatch(!showOptions);
                          }}
                        ></WarningIcon>
                      </Tooltip>
                    ) : null}
                  </div>

                  <div
                    style={{
                      position: 'absolute',
                      zIndex: 10,
                      right: '25px',
                      top: '4px',
                    }}
                  >
                    <Tooltip
                      title="Available key"
                      arrow
                      placement="top"
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: '12px',
                          },
                        },
                      }}
                    >
                      <InfoOutlined
                        style={{
                          marginLeft: '4px',
                          cursor: 'pointer',
                          marginTop: '4px',
                          color: 'grey', //isHelpHovered ? 'LightGrey' : 'grey',
                          fontSize: '1em',
                          transition: 'color 0.3s, font-size 0.3s',
                        }}
                        // onMouseEnter={() => {
                        //   setIsHelpHovered(true);
                        // }}
                        // onMouseLeave={() => {
                        //   setIsHelpHovered(false);
                        // }}
                        onClick={(event) => {
                          setAnchorHelpElm(event.currentTarget);
                          setShowOptions(!showOptions);
                        }}
                      />
                    </Tooltip>
                  </div>

                  <Select
                    sx={{
                      fontSize: '13px',
                      height: '1.5rem',
                      backgroundColor: 'white',
                      color: 'grey',
                      paddingLeft: '20px',
                      width: '100%',
                      textAlign: 'start', //**************************

                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#2bb9bb',
                        color: '#2bb9bb',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#2bb9bb',
                        color: '#2bb9bb',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '20px',
                        paddingRight: '2px',
                        cursor: 'pointer',
                      },
                      '&.Mui-focused .MuiSvgIcon-root ': {
                        fill: '#2bb9bb !important',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          '& .MuiMenuItem-root.Mui-selected': {
                            backgroundColor: 'rgba(43, 185, 187, 0.1) !important', // Force background color
                          },
                          '& .MuiMenuItem-root.Mui-selected:hover': {
                            backgroundColor: 'rgba(43, 185, 187, 0.2) !important', // Change hover state for selected item
                          },
                        },
                      },
                    }}
                    style={{ borderRadius: '4px' }}
                    label="Select Maps"
                    value={chartProp.properties[propKey].Geo.geoMapKey || 'name'}
                    onChange={(e) => {
                      dispatch(changeGeoKey(propKey, e.target.value));
                      handleGeoMapUnMatchedReset();
                    }}
                  >
                    {mapKeys.map((key: any, index: number) => (
                      <MenuItem sx={menuItemStyle} value={key} key={index}>
                        {key}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </FormControl>

              <GoeHelp
                propKey={propKey}
                open={showOptions}
                anchorElement={anchorHelpElm}
                mapJSON={mapJSON}
                handleClose={handleClose}
              ></GoeHelp>
              <GoeMismatch
                propKey={propKey}
                misMatchList={misMatchList}
                open={showOptionsMismatch}
                anchorElement={anchorMismatchElm}
                geoJSON={mapJSON}
                handleClose={handleCloseWarningMismatch}
              ></GoeMismatch>
            </div>
          )}
        {!currentCalculationSession && (
          <div
            className={uID ? 'overrideContainer dropzone-container' : 'dropzone-container'}
            style={{ overflowX: 'hidden', paddingRight: '0.5rem', height: '100%' }}
          >
            {!currentCalculationSession &&
              dropZones.map((zone: any, zoneI: any) =>
                uID ? (
                  zone !== 'Measure' && zone !== 'Y' && zone !== 'X' && zone !== 'Split By' ? (
                    <DropZone
                      bIndex={zoneI}
                      name={zone}
                      propKey={propKey}
                      key={zoneI}
                      uID={uID}
                      tabTileProps={tabTileProps}
                      chartProp={chartProp}
                      lastAxisName={dropZones[dropZones.length - 1]}
                    />
                  ) : null
                ) : (
                  <DropZone
                    bIndex={zoneI}
                    name={zone}
                    propKey={propKey}
                    key={zoneI}
                    uID={uID}
                    tabTileProps={tabTileProps}
                    chartProp={chartProp}
                    lastAxisName={dropZones[dropZones.length - 1]}
                  />
                ),
              )}
          </div>
        )}

        {uID && (
          <>
            {/* <span
              style={{
                borderTop: '2px solid rgba(224, 224, 224, 1)',
                flex: 1,
                paddingBottom: '2px',
                marginRight: '0.5rem',
              }}
            ></span> */}

            <div
              className="chartAxes-button-container"
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                columnGap: '0.3rem',
                position: 'sticky',
                bottom: '0.3rem',
                marginTop: '8px',
                height: '10%',
                paddingLeft: '0.3rem',
                paddingRight: '0.3rem',
              }}
            >
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={isExtraMeasure ? handleExtraMeasureOverrideRemove : handleOverrideRemove}
                sx={{
                  fontSize: '10px',
                  boxShadow: 'none',
                  border: '2px solid #b6b6b6',
                  borderRadius: '2px',
                  textTransform: 'initial',
                  '&:hover': {
                    color: 'white',
                    backgroundColor: 'red',
                    border: '2px solid red',
                    boxShadow: '0px 0px 2px 1px #e32e2e',
                  },
                }}
              >
                Remove
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={() => {
                  dispatch(SetModifyOptions(propKey, 'filter_override_close', null));
                  if (isExtraMeasure) {
                    dispatch(enableOverrideForExtraMeasure(propKey, ''));
                    dispatch(removeChartAxesForUID(propKey, uID));
                  } else {
                    dispatch(enableOverrideForUIDAction(propKey, ''));
                    dispatch(removeChartAxesForUID(propKey, uID));

                    // Find Measure field
                    // const bIndex = ChartsInfo[chartProp.properties[propKey].chartType]
                    //   .dropZones.findIndex((item: any) => item.name === "Measure");

                    // const itemIndex = chartProp.properties[propKey].chartAxes[bIndex]
                    //   .fields.findIndex((item: any) => item.uId === uID);

                    // const field = chartProp.properties[propKey].chartAxes[bIndex].fields[itemIndex];

                    // if (field) {
                    //   let tempField = JSON.parse(JSON.stringify(field));

                    //   // if override does NOT exist → reset ignore filter
                    //   if (!tempField.override) {
                    //     tempField.disableReportFilterForOverride = false;
                    //   }

                    //   dispatch(editChartPropItem("updateQuery", {
                    //     propKey,
                    //     binIndex: bIndex,
                    //     itemIndex,
                    //     item: tempField,
                    //     currentChartAxesName: "chartAxes"
                    //   }));
                    // }
                  }
                }}
                sx={{
                  fontSize: '10px',
                  boxShadow: 'none',
                  border: '2px solid #b6b6b6;',
                  borderRadius: '2px',
                  textTransform: 'initial',
                  '&:hover': {
                    color: 'white',
                    backgroundColor: '#b6b6b6',
                    boxShadow: '0px 0px 2px 1px rgb(199, 199, 199)',
                  },
                }}
              >
                Back
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={handleSave}
                sx={{
                  fontSize: '10px',
                  boxShadow: 'none',
                  border: '2px solid #2bb9bb',
                  borderRadius: '2px',
                  textTransform: 'initial',
                  color: '#2bb9bb',
                  '&:hover': {
                    color: 'white',
                    fontWeight: 'bold',
                    backgroundColor: '#2bb9bb',
                    boxShadow: '0px 0px 2px 1px #2bb9bb',
                  },
                }}
              >
                Save
              </Button>
            </div>
          </>
        )}
        {currentCalculationSession && <CalculationFunctionListHolder />}

        {
          uID ? null : <ChartData tabId={tabId} tileId={tileId} screenFrom="Chartaxes"></ChartData> //TODO chartdata
        }

        {alert && (
          <NotificationDialog
            onCloseAlert={() => setAlert(null)}
            severity={alert.severity}
            testMessage={alert.message}
            openAlert={!!alert}
          />
        )}
      </div>
    )
  );
};

export default ChartAxes;
